import React, { useState } from "react";
import moment from "moment";
import styles from "./ProfileDrawerHeader.module.scss";
import {
  Avatar,
  Grid,
  Popover,
  Button as AntdButton,
  Tooltip,
  Badge,
  Modal,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  CrownOutlined,
  DownloadOutlined,
  ExclamationCircleFilled,
  FlagFilled,
  FlagOutlined,
  MailOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "components/Buttons/Buttons";
import {
  generateProfilePDF,
  toggleLoyaltyStatus,
  toggleProfileFlag,
} from "services/guests.services";
import EmailModal from "components/EmailModal/EmailModal";
import AddEmailAddresses from "./AddEmailAddresses";
import FlagReasonModal from "./FlagReasonModal";

const { useBreakpoint } = Grid;

export default function ProfileDrawerHeader({
  user,
  onBookingClick,
  onClose,
  showEmailSendModal,
  setShowEmailModal,
  refreshCustomer,
}) {
  const dispatch = useDispatch();

  const screens = useBreakpoint();
  const isSmallDevice = !screens.sm;

  const [showSendEmail, setshowSendEmail] = useState(false);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [isTogglingFlag, setIsTogglingFlag] = useState(false);
  const [isTogglingLoyalty, setIsTogglingLoyalty] = useState(false);

  const isFlagged = user?.isCustomerProfileFlagged;

  const isLoyaltyEligible = user?.enrolledIntoLoyaltyProgram;

  const currentStaff = useSelector((state) => state.user.currentUser);

  const hasFlagPermission = React.useMemo(() => {
    return currentStaff?.permissions?.find(
      (permission) => permission.permissionName === "Flag Management"
    );
  }, [currentStaff]);

  async function flagAPICall(notes) {
    setIsTogglingFlag(true);
    return dispatch(
      toggleProfileFlag(user?.customerId, {
        notes,
        flagBit: isFlagged ? false : true,
      })
    )
      .then((res) => {
        if (res?.status === "success") {
          setShowFlagModal(false);
          refreshCustomer?.();
        }
      })
      .catch((e) => {
        console.log("e ", e);
      })
      .finally(() => {
        setIsTogglingFlag(false);
      });
  }

  function renderBookingButton() {
    return (
      <Button
        rounded
        style={{
          height: isSmallDevice ? 40 : 30,
          marginRight: 12,
          padding: "0px 12px",
          width: isSmallDevice ? "100%" : "auto",
        }}
        className="appBookAppointmentButton"
        onClick={onBookingClick}
      >
        Book an Appointment
      </Button>
    );
  }

  function handleFlagClick() {
    // if flagged then show the confirmation prompt to unflag
    if (isFlagged) {
      Modal.confirm({
        className: styles.confirmModal,
        centered: true,
        width: 460,
        autoFocusButton: null,
        icon: <ExclamationCircleFilled style={{ color: "#faad14" }} />,
        title: "Unflag The Profile?",
        okText: "Yes, Unflag",
        cancelText: "Cancel",
        content: "Are you sure you want to unflag this profile?",
        onOk: async (close) => {
          await flagAPICall();
          close();
        },
        closeIcon: <CloseCircleOutlined />,
        maskClosable: true,
        closable: true,
      });
    } else {
      setShowFlagModal(true);
    }
  }

  function handleLoyaltyClick() {
    const nextEligible = !isLoyaltyEligible;

    Modal.confirm({
      className: styles.confirmModal,
      centered: true,
      width: 460,
      autoFocusButton: null,
      icon: nextEligible ? (
        <StarFilled style={{ color: "#52c41a" }} />
      ) : (
        <ExclamationCircleFilled style={{ color: "#faad14" }} />
      ),
      title: `${nextEligible ? "Enable" : "Disable"
        } Reward Program Eligibility?`,
      okText: nextEligible ? "Yes, Enable" : "Yes, Disable",
      cancelText: "Cancel",
      content: `Are you sure you want to ${nextEligible ? "enable" : "disable"
        } reward program eligibility for this profile?`,
      onOk: async (close) => {
        setIsTogglingLoyalty(true);
        try {
          const res = await dispatch(
            toggleLoyaltyStatus(user?.customerId, nextEligible)
          );
          if (res?.status === "success") {
            refreshCustomer?.();
          }
        } finally {
          setIsTogglingLoyalty(false);
        }
        close();
      },
      closeIcon: <CloseCircleOutlined />,
      maskClosable: true,
      closable: true,
    });
  }

  return (
    <>
      <div className={styles.profileDrawerHeader}>
        <div className={styles.firstRow}>
          <div className={styles.firstRowLeftSection}>
            <Tooltip title={isFlagged ? "Profile is flagged" : null}>
              <Badge
                count={
                  isFlagged ? <FlagFilled style={{ color: "#f5222d" }} /> : 0
                }
              >
                <div
                  className={styles.avatar}
                  style={{
                    backgroundColor: isFlagged ? "#f5222d" : "#ccc",
                  }}
                >
                  {user?.firstName[0]}
                  {user?.lastName[0]}
                </div>
              </Badge>
            </Tooltip>
            <Tooltip
              title={`${user?.firstName} ${user?.lastName} (Customer ID: ${user?.customerId})`}
            >
              <p
                className={styles.name}
                style={{ color: isFlagged ? "#ff4d4f" : "#000" }}
              >
                {user?.firstName} {user?.lastName}
              </p>
            </Tooltip>
            <Popover
              content={`User is ${user?.isRegistrationComplete ? "active" : "not active"
                }`}
            >
              {user?.isRegistrationComplete ? (
                <CheckCircleOutlined
                  style={{ fontSize: 22, color: "#52c41a", marginLeft: 8 }}
                />
              ) : (
                <CloseCircleOutlined
                  style={{ fontSize: 22, color: "#f5222d", marginLeft: 8 }}
                />
              )}
            </Popover>
            {user?.hasMembership && (
              <Popover content={"User has membership"}>
                <CrownOutlined
                  style={{
                    fontSize: 28,
                    paddingLeft: 4,
                    color: "#ffa900",
                  }}
                />
              </Popover>
            )}
          </div>
          <div style={{ display: "flex", alignSelf: "flex-start" }}>
            {!isSmallDevice && renderBookingButton()}
            <CloseOutlined style={{ paddingTop: 6 }} onClick={onClose} />
          </div>
        </div>
        <div className={styles.secondRow} style={{ flexDirection: isSmallDevice ? "column" : "row", gap: isSmallDevice ? 10 : 0 }}>
          <div style={{ display: 'flex', flexDirection: isSmallDevice ? "column-reverse" : 'column', gap: 10 }}>
            {isSmallDevice && renderBookingButton()}
            {
              <div style={{ display: "flex", gap: 10, }}>
                <p style={{ margin: 0, fontSize: 14 }}>Customer ID: {user?.customerId}</p>
                {user?.hasActiveMobileApp &&
                  <Tooltip
                    title={
                      user?.mobileAppLastVerifiedOn
                        ? `Last verified on ${moment.utc(user.mobileAppLastVerifiedOn).local().format("MMM DD, YYYY h:mm A")}`
                        : "App Installed"
                    }
                    overlayInnerStyle={{fontSize: 13}}
                  >
                    <div className={styles.appInstalledPill}>
                      <StarFilled style={{ color: "#52c41a" }} />
                      <span> App Installed </span>
                    </div>
                  </Tooltip>
                }
              </div>
            }

          </div>
          <div className={styles.controls} style={{ justifyContent: isSmallDevice ? 'space-between' : 'flex-end' }}>
            <Tooltip title="Download PDF">
              <AntdButton
                size="small"
                shape="round"
                type="primary"
                style={{ width: isSmallDevice ? "100%" : "auto", height: isSmallDevice ? 30 : 24, maxWidth: 84 }}
                icon={<DownloadOutlined />}
                onClick={async () => {
                  const report = await dispatch(
                    generateProfilePDF(user?.customerId, [], true)
                  );
                  if (report?.status === "success") {
                    window.open(report?.data, "_blank");
                  }
                }}
              />
            </Tooltip>

            <Tooltip title="Email PDF to Addresses">
              <AntdButton
                size="small"
                shape="round"
                type="primary"
                style={{ width: isSmallDevice ? "100%" : "auto", height: isSmallDevice ? 30 : 24, maxWidth: 84 }}
                icon={<MailOutlined />}
                onClick={() => setshowSendEmail(true)}
              />
            </Tooltip>
            {hasFlagPermission && (
              <Tooltip title={`${isFlagged ? "Unflag" : "Flag"} Profile`}>
                <AntdButton
                  danger
                  size="small"
                  shape="round"
                  style={{ width: isSmallDevice ? "100%" : "auto", height: isSmallDevice ? 30 : 24, maxWidth: 84 }}
                  className={isFlagged ? styles.flaggedIcon : ""}
                  type={isFlagged ? "primary" : "default"}
                  loading={isTogglingFlag}
                  icon={<FlagOutlined />}
                  onClick={handleFlagClick}
                />
              </Tooltip>
            )}
            <Tooltip
              title={`${isLoyaltyEligible ? "Disable" : "Enable"
                } Reward Program Eligibility`}
            >
              <AntdButton
                size="small"
                shape="round"
                loading={isTogglingLoyalty}
                style={{ width: isSmallDevice ? "100%" : "auto", height: isSmallDevice ? 30 : 24, maxWidth: 84 }}
                className={`${styles.loyaltyIcon} ${isLoyaltyEligible ? styles.loyaltyIconEligible : ""}`}
                icon={<StarOutlined />}
                onClick={handleLoyaltyClick}
              />
            </Tooltip>
          </div>
        </div>
      </div >

      <AddEmailAddresses
        guestId={user?.customerId}
        visible={showSendEmail}
        onClose={() => setshowSendEmail(false)}
      />
      <EmailModal
        visible={showEmailSendModal}
        onClose={() => setShowEmailModal(false)}
        user={user}
        saveText="Send"
      />
      <FlagReasonModal
        visible={showFlagModal}
        onSave={flagAPICall}
        onCancel={() => setShowFlagModal(false)}
      />
    </>
  );
}
